/**
 * Pure calculation functions for Pro Forma forecasting
 * 
 * These functions implement the calculation logic described in the Pro Forma CSV:
 * - Fixed accounts: Divide base period total evenly across forecast months
 * - Variable accounts: Scale based on stat driver ratio
 */

/**
 * Calculate monthly value for a Fixed account
 * Fixed accounts spread the base period total evenly across forecast months
 * 
 * @param {number} basePeriodTotal - The account's total value in the base period
 * @param {number} forecastMonthCount - Number of months in the forecast period
 * @returns {number} The monthly value (base total / month count)
 * 
 * Example: $17,612 base total ÷ 12 months = $1,468/month
 */
export const calculateFixedValue = (basePeriodTotal, forecastMonthCount) => {
  if (!forecastMonthCount || forecastMonthCount === 0) {
    return 0;
  }
  return basePeriodTotal / forecastMonthCount;
};

/**
 * Calculate monthly value for a Variable account
 * Variable accounts scale based on the stat driver ratio for that month
 * 
 * @param {number} accountBasePeriodTotal - The account's total value in the base period
 * @param {number} monthStatDriverValue - The stat driver value for this specific month
 * @param {number} statDriverBasePeriodTotal - The stat driver's total in the base period
 * @returns {number} The calculated monthly value
 * 
 * Formula: (Forecast Month Stat Driver / Base Period Stat Driver Total) × Account Base Period Total
 * 
 * Example: 
 *   Base wRVUs = 51,520
 *   January forecast wRVUs = 500
 *   Account base total = $11,857,077
 *   Result: (500 / 51,520) × $11,857,077 = $115,073
 */
export const calculateVariableValue = (accountBasePeriodTotal, monthStatDriverValue, statDriverBasePeriodTotal) => {
  if (!statDriverBasePeriodTotal || statDriverBasePeriodTotal === 0) {
    return 0;
  }
  const ratio = monthStatDriverValue / statDriverBasePeriodTotal;
  return accountBasePeriodTotal * ratio;
};

/**
 * Calculate all monthly values for an account based on its type (Fixed or Variable)
 * 
 * @param {Object} account - The account object with basePeriodTotal
 * @param {string} fixedOrVariable - 'F' for Fixed, 'V' for Variable
 * @param {Array<string>} forecastMonths - Array of month labels (e.g., ['Jan 2025', 'Feb 2025', ...])
 * @param {Object} statDriver - Stat driver data (only used for Variable accounts)
 * @param {number} statDriver.basePeriodTotal - Stat driver base period total
 * @param {Object} statDriver.forecastMonthly - Object with month labels as keys and values
 * @returns {Object} Object with month labels as keys and calculated values
 * 
 * Example return:
 * {
 *   'Jan 2025': 1468,
 *   'Feb 2025': 1468,
 *   ...
 * }
 */
export const calculateAccountMonthlyValues = (account, fixedOrVariable, forecastMonths, statDriver) => {
  const monthlyValues = {};
  
  if (fixedOrVariable === 'F') {
    // Fixed: divide evenly across all months
    const monthlyValue = calculateFixedValue(account.basePeriodTotal, forecastMonths.length);
    forecastMonths.forEach(month => {
      monthlyValues[month] = monthlyValue;
    });
  } else if (fixedOrVariable === 'V') {
    // Variable: scale by stat driver ratio for each month
    forecastMonths.forEach(month => {
      const monthStatDriverValue = statDriver.forecastMonthly[month] || 0;
      monthlyValues[month] = calculateVariableValue(
        account.basePeriodTotal,
        monthStatDriverValue,
        statDriver.basePeriodTotal
      );
    });
  }
  
  return monthlyValues;
};
